import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { MagnifyingGlass, LinkSimple, CheckCircle, User as UserIcon } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const fmt = (n) => new Intl.NumberFormat("de-DE").format(n || 0);

export default function LinkCharacter({ onLinked }) {
  const { refresh } = useAuth();
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async (query) => {
    try {
      const r = await api.get("/character/link/candidates", { params: query ? { q: query } : {} });
      setCandidates(r.data);
    } catch { toast.error("Fehler beim Laden"); }
  };

  useEffect(() => { search(""); }, []);
  useEffect(() => {
    const t = setTimeout(() => search(q), 300);
    return () => clearTimeout(t);
     
  }, [q]);

  const pick = async (esxId, name) => {
    setBusy(true);
    try {
      await api.post("/character/link", { esx_character_id: esxId });
      toast.success(`Verknüpft mit ${name}`);
      await refresh();
      onLinked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Verknüpfen");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-volt/40 bg-volt/5 p-6" data-testid="link-character-widget">
      <div className="flex items-start gap-3 mb-5">
        <LinkSimple size={22} className="text-volt mt-1" weight="duotone" />
        <div>
          <div className="font-mono text-[10px] text-volt uppercase tracking-widest">Setup benötigt</div>
          <h3 className="font-display font-bold text-xl">Verknüpfe deinen In-Game Charakter</h3>
          <p className="text-sm text-white/60 mt-1">Wähle deinen echten RP-Char aus der Liste unten. Danach zeigt die App überall deinen RP-Namen und deine echten ESX-Daten (Auto, Bank, Job).</p>
        </div>
      </div>

      <div className="border border-tactical-border bg-black/40 p-3 flex items-center gap-2 mb-3">
        <MagnifyingGlass size={16} className="text-white/40" />
        <input
          data-testid="link-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Suche nach Vor- oder Nachname..."
          className="flex-1 bg-transparent outline-none font-mono text-sm text-white placeholder:text-white/30"
        />
        <div className="font-mono text-[10px] text-white/40">{candidates.length}</div>
      </div>

      <div className="grid md:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
        {candidates.map(c => (
          <button
            key={c.id}
            data-testid={`link-pick-${c.id}`}
            disabled={busy}
            onClick={() => pick(c.id, `${c.first_name} ${c.last_name}`)}
            className="border border-tactical-border bg-tactical-surface hover:border-volt text-left p-3 transition-colors duration-150 disabled:opacity-50 group"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <UserIcon size={14} className="text-white/40" />
                  <div className="font-display font-bold text-white truncate">{c.first_name} {c.last_name}</div>
                </div>
                <div className="font-mono text-[10px] text-white/40 mt-1">
                  {c.job_grade_label} · {c.job?.toUpperCase()}
                </div>
                <div className="font-mono text-[10px] text-white/50 mt-1">
                  ${fmt((c.cash || 0) + (c.bank || 0))} · Level {c.level || 1}
                </div>
              </div>
              {c._linked_by > 0 && (
                <div className="font-mono text-[9px] text-tactical-warn uppercase tracking-widest flex-shrink-0">
                  <CheckCircle size={10} className="inline mr-0.5" /> gepickt
                </div>
              )}
            </div>
          </button>
        ))}
        {candidates.length === 0 && (
          <div className="col-span-2 text-center py-8 font-mono text-white/40 text-sm">
            Keine ESX-Charaktere gefunden. Führe im Admin-Panel zuerst "Jetzt Syncen" aus.
          </div>
        )}
      </div>
    </div>
  );
}
